import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useContext,
} from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { Button } from "components/Graphs/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { Spinner } from "components/UI-lib";
import { filterForGPUInstances } from "components/Environment/Create/EnvironmentCreatePublic";
import { GPUInstanceType } from "types/InstanceTypes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { Badge } from "components/Graphs/Badge";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { useQueryClient } from "@tanstack/react-query";
import type { SearchLaunchablesResponse } from "server/devplane/launchables/api";
import { launchableSearchQueryOptions } from "server/devplane/launchables/queries";

interface SearchBarProps {
  onSearch: (
    results: SearchLaunchablesResponse | null,
    append?: boolean,
  ) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  selectedGPU: string;
  setSelectedGPU: (value: string) => void;
  selectedCloudProvider: string;
  setSelectedCloudProvider: (value: string) => void;
  currentCursor?: string;
  onLoadMoreRef?: React.MutableRefObject<(() => void) | null>;
  isLoadingMore?: boolean;
  setIsLoadingMore?: (loading: boolean) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  searchValue,
  setSearchValue,
  selectedGPU,
  setSelectedGPU,
  selectedCloudProvider,
  setSelectedCloudProvider,
  currentCursor,
  onLoadMoreRef,
  isLoadingMore,
  setIsLoadingMore,
}) => {
  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allGPUInstances, setAllGPUInstances] = useState<GPUInstanceType[]>([]);

  const search = useCallback(
    async (cursor?: string, append: boolean = false) => {
      if (append && setIsLoadingMore) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Build filters inline
      const filter: Record<string, string[]> = {};
      if (selectedGPU !== "Any") {
        filter["gpu_name"] = [selectedGPU.trim()];
      }
      if (selectedCloudProvider !== "Any") {
        filter["provider"] = [selectedCloudProvider.toLowerCase().trim()];
      }

      try {
        const results = await queryClient.fetchQuery(
          launchableSearchQueryOptions({
            query: searchValue.trim(),
            filters: filter,
            pageSize: 21,
            cursor,
          }),
        );
        onSearch(results, append);
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : "Failed to search launchables",
        );
      } finally {
        if (append && setIsLoadingMore) {
          setIsLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      searchValue,
      selectedGPU,
      selectedCloudProvider,
      onSearch,
      queryClient,
      setIsLoadingMore,
    ],
  );

  const loadMore = useCallback(() => {
    if (currentCursor && !loading && !isLoadingMore) {
      search(currentCursor, true);
    }
  }, [currentCursor, loading, isLoadingMore, search]);

  // Expose loadMore function to parent via ref
  useEffect(() => {
    if (onLoadMoreRef) {
      onLoadMoreRef.current = loadMore;
    }
  }, [loadMore, onLoadMoreRef]);

  // Clear search results & input values when the component unmounts
  useEffect(() => {
    return () => {
      onSearch(null);
      setSelectedGPU("Any");
      setSelectedCloudProvider("Any");
      setSearchValue("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      searchValue === "" &&
      selectedGPU === "Any" &&
      selectedCloudProvider === "Any"
    ) {
      onSearch(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, selectedGPU, selectedCloudProvider]);

  // Auto-trigger search when filters change
  useEffect(() => {
    // Only auto-search if we have either a search value or active filters
    if (
      searchValue.trim() ||
      selectedGPU !== "Any" ||
      selectedCloudProvider !== "Any"
    ) {
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGPU, selectedCloudProvider]);

  const handleSearch = () => {
    if (searchValue.trim()) {
      search();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  // Initialize from query parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gpuType = params.get("gpuType");
    const cloud = params.get("cloud");

    if (gpuType) {
      setSelectedGPU(decodeURIComponent(gpuType));
    }
    if (cloud) {
      setSelectedCloudProvider(decodeURIComponent(cloud));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const accountInstances = instanceTypesContext.GPUInstancesAvailable
      ? filterForGPUInstances(instanceTypesContext.GPUInstancesAvailable)
      : [];

    const gpuInstances = accountInstances.filter(
      (instance) =>
        !instance.supported_gpus[0].name.includes("5090") &&
        !instance.supported_gpus[0].name.includes("4090"),
    );

    setAllGPUInstances(gpuInstances);
  }, [instanceTypesContext.GPUInstancesAvailable]);

  const gpuNames = useMemo(() => {
    const names = new Set(
      allGPUInstances.map((instance) => instance.supported_gpus[0].name),
    );
    if (selectedGPU !== "Any") {
      names.add(selectedGPU);
    }
    const sortedNames = Array.from(names).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
    return ["Any", ...sortedNames];
  }, [allGPUInstances, selectedGPU]);

  const cloudProviders = useMemo(() => {
    const providers = new Set(
      allGPUInstances
        .map((instance) => (instance.cloud || instance.provider)?.toUpperCase())
        .filter((p): p is string => Boolean(p)),
    );
    if (selectedCloudProvider !== "Any") {
      providers.add(selectedCloudProvider.toUpperCase());
    }
    const sortedProviders = Array.from(providers).sort();
    return ["Any", ...sortedProviders];
  }, [allGPUInstances, selectedCloudProvider]);

  const hasActiveFilters =
    selectedGPU !== "Any" || selectedCloudProvider !== "Any";

  return (
    <div className="w-full space-y-2">
      {/* Search bar - single line */}
      <div className="flex items-center gap-3 w-full rounded-xl px-4 py-2 bg-secondaryBg border border-primaryBorder focus-within:border-highlight">
        {/* Input field */}
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSearch();
            }
          }}
          placeholder="Search by name, containers, models, workflows, gpus..."
          className="flex-1 bg-transparent border-none text-[16px] text-white focus:outline-none focus:ring-0 placeholder:text-zinc-600"
        />

        {/* Right side buttons */}
        <div className="flex items-center gap-2">
          {/* Filter button */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 rounded-lg p-0 hover:bg-zinc-800"
              >
                <SlidersHorizontal className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 pb-4" align="end">
              <div className="space-y-4">
                <div className="space-y-1">
                  <h4 className="font-medium leading-none">Search Filters</h4>
                  <p className="text-xs text-muted-foreground">
                    Customize your search results even further
                  </p>
                  <hr className="w-[100%] mb-3 mt-3 dark:border dark:border-zinc-800" />
                </div>
                <div className="space-y-4">
                  <div>
                    <h5 className="text-xs mb-2">GPU Name:</h5>
                    <Select value={selectedGPU} onValueChange={setSelectedGPU}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select GPU..." />
                      </SelectTrigger>
                      <SelectContent>
                        {gpuNames.map((gpu) => (
                          <SelectItem key={gpu} value={gpu}>
                            {gpu}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <h5 className="text-xs mb-2">Cloud Provider:</h5>
                    <Select
                      value={selectedCloudProvider}
                      onValueChange={setSelectedCloudProvider}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Cloud Provider..." />
                      </SelectTrigger>
                      <SelectContent>
                        {cloudProviders.map((cloudProvider) => (
                          <SelectItem key={cloudProvider} value={cloudProvider}>
                            {cloudProvider}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Search/Loading button */}
          {loading ? (
            <Button
              className="h-8 w-8 rounded-xl p-0 flex items-center justify-center"
              variant="primary"
              disabled
            >
              <Spinner type="primary" />
            </Button>
          ) : (
            <Button
              onClick={handleSearch}
              className="h-8 w-8 rounded-xl p-0"
              variant="primary"
              disabled={!searchValue.trim()}
            >
              <Search className="h-4 w-4 text-white" />
            </Button>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="pl-2 text-xs text-red-500">
          {error}
        </p>
      )}

      {/* Active filters - second row (only shown when filters are active) */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 pl-2">
          <span className="text-xs text-secondary">Active filters:</span>
          {selectedGPU !== "Any" && (
            <Badge variant="neutral" className="flex items-center gap-1">
              <span className="text-xs text-primary">{selectedGPU}</span>
              <button
                onClick={() => setSelectedGPU("Any")}
                className="ml-1 hover:text-white"
              >
                ×
              </button>
            </Badge>
          )}
          {selectedCloudProvider !== "Any" && (
            <Badge variant="neutral" className="flex items-center gap-1">
              <span className="text-xs text-primary">
                {selectedCloudProvider}
              </span>
              <button
                onClick={() => setSelectedCloudProvider("Any")}
                className="ml-1 hover:text-white"
              >
                ×
              </button>
            </Badge>
          )}
          <button
            onClick={() => {
              setSelectedGPU("Any");
              setSelectedCloudProvider("Any");
            }}
            className="text-xs text-secondary hover:text-white underline ml-2"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};
